import { useState, useCallback, useRef, useEffect } from 'preact/hooks';

const TOKEN_KEY = 'cryptodash_session_token';

/** Read stored session token */
export function getSessionToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

/** Save session token */
export function setSessionToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token);
}

/** Remove session token */
export function clearSessionToken(): void {
  localStorage.removeItem(TOKEN_KEY);
}

interface LoginScreenProps {
  onAuthenticated: () => void;
}

export function LoginScreen({ onAuthenticated }: LoginScreenProps) {
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleSubmit = useCallback(async (e: Event) => {
    e.preventDefault();
    if (!code.trim() || loading) return;

    setLoading(true);
    setError('');

    try {
      const res = await fetch('/api/auth/verify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: code.trim() }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({ error: 'Server error' }));
        setError(data.error ?? 'Invalid code');
        setCode('');
        inputRef.current?.focus();
        return;
      }

      const { token } = await res.json();
      setSessionToken(token);
      onAuthenticated();
    } catch {
      setError('Connection error');
    } finally {
      setLoading(false);
    }
  }, [code, loading, onAuthenticated]);

  return (
    <div class="login-screen">
      <form class="login-card" onSubmit={handleSubmit}>
        <div class="login-icon">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="var(--lime)" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
          </svg>
        </div>

        <h1 class="login-title">CRYPTODASH</h1>
        <p class="login-subtitle">Enter access code</p>

        <input
          ref={inputRef}
          type="password"
          inputMode="numeric"
          class="login-input"
          placeholder="Access code"
          value={code}
          onInput={(e) => setCode((e.target as HTMLInputElement).value)}
          disabled={loading}
          autocomplete="off"
        />

        {error && <div class="login-error">{error}</div>}

        <button type="submit" class="login-btn" disabled={loading || !code.trim()}>
          {loading ? 'Verifying...' : 'Enter'}
        </button>
      </form>
    </div>
  );
}
